import * as store from '@/store';
import sdk from '@/sdk';

import {getMorphoService, serviceNames} from '@/rte/editor';
import KuAuthority from '@/auth/KuAuthority';
import {ServerConfig} from '@/config/routers';
import {PageScope, InheritRepo, RepositoryScope} from '@/Constant';

import type {IDoc, ISideBarItem, IAttachItem} from '../types/doc';
import {DOC_MODE} from '../types/doc';

export interface IDocData {
    info: string;
    data: any;
}

export interface IEvent {
    selection: any;
    nodes: any[];
    plainText: string;
    markdown: string;
    html: string;
    collapsed: boolean;
}

let docSelectionData: IEvent = {
    selection: [],
    nodes: [],
    plainText: '',
    markdown: '',
    html: '',
    collapsed: true,
};

export let selectionChangeCallback = (event: IEvent) => {};

function formatSideBarNode(node: any = {}) {
    const {url, docGuid, parentGuid, isLeaf, name, children} = node;
    const newNode: ISideBarItem = {
        docUrl: `${window.location.origin}${url}`,
        docId: docGuid,
        parentDocId: parentGuid,
        title: name,
        isLeaf,
        children: [],
    };

    if (children && children.length > 0) {
        newNode.children = node.children.map((item: any) => formatSideBarNode(item));
    }

    return newNode;
}

function getAttachDataByJSON(json: any) {
    if (!Array.isArray(json)) {
        return [];
    }

    let result: IAttachItem[] = [];
    try {
        const stack = [...json];

        while (stack.length > 0) {
            const node = stack.pop();
            if (!node) {
                continue;
            }

            const {type, fileId, fileInfo} = node;
            if (type === 'attachment') {
                result.push({
                    id: String(fileId),
                    url: '',
                    type: fileInfo.type,
                    size: fileInfo.size,
                    name: fileInfo.name,
                    extension: fileInfo.extension,
                });
            }

            if (Array.isArray(node.children)) {
                node.children.forEach((el: any) => {
                    stack.push(el);
                });
            }
        }
    } catch {
        result = [];
    }
    return result;
}

async function getAttachUrl(fileId: string) {
    if (!fileId) {
        return '';
    }

    if (fileId.includes('http')) {
        return fileId;
    }

    const curDocument = store.getData(store.KEYS.CUR_DOCUMENT) || {};

    try {
        const result = await sdk.serverBase.request({
            method: 'get',
            url: ServerConfig.rte + '/wiki/attach/attachments/downloadUrl',
            params: {
                docGuid: curDocument.docGuid || '',
                versionId: '2',
                attachId: fileId,
            },
            timeout: 600000,
        });

        let url = '';
        if (result.downloadUrl) {
            if (location.href.includes('ku.baidu-int.com') && result.downloadUrl.indexOf('bj.bcebos.com/v1') !== -1) {
                const t = new Date().getTime();
                url = `${result.downloadUrl}?t=${t}`;
            } else {
                url = result.downloadUrl;
            }
        }
        return url;
    } catch {
        return '';
    }
}

export const doc: IDoc = {
    // 获取文档长度
    getDocLength: () => {
        const editorRef = store.getEditorRef();
        const editor: any = editorRef.current; // 临时any大法
        const len = editor ? editor.getDocLength() : 0;
        return len;
    },
    // 更新文档选中内容
    setDocSelectionData: (event: IEvent) => {
        docSelectionData = event;
    },
    // 获取文档选中内容
    getSelectionContent: ({type}: {type: 'json' | 'markdown' | 'text' | 'html'}) => {
        if (docSelectionData.collapsed) {
            return null;
        }
        if (type === 'json') {
            return docSelectionData.nodes;
        }
        if (type === 'markdown') {
            return docSelectionData.markdown;
        }
        if (type === 'text') {
            return docSelectionData.plainText;
        }
        if (type === 'html') {
            return docSelectionData.html;
        }
        return null;
    },
    // 监听文档选中内容变化
    onSelectionChange: (callback: (event: IEvent) => void) => {
        selectionChangeCallback = callback;
    },
    // 获取文档内容
    getDocContent: async (options) => {
        const {type, inlineStyle = false, brCharacterInTable = ''} = options;

        return new Promise((resolve, reject) => {
            let retryCount = 0;

            const getContent = async () => {
                const editorRef = store.getEditorRef();

                while (!editorRef.current || typeof editorRef.current.getEditorInstance !== 'function') {
                    if (retryCount >= 3) {
                        reject(new Error('尝试获取编辑器实例次数超过限制'));
                        return;
                    }

                    await new Promise((innerResolve) => setTimeout(innerResolve, 500));
                    console.log(`重试获取编辑器实例, retry: ${retryCount}`);
                    retryCount++;
                }

                const instance = editorRef.current.getEditorInstance();

                if (!instance) {
                    // todo 这里需要马哥封装一个error对象，定义好code
                    reject(new Error('获取文档内容失败：getEditorInstance error'));
                }

                const mdSerializerService = getMorphoService(serviceNames.IMarkdownSerializerService);
                const htmlSerializerService = getMorphoService(serviceNames.IHtmlSerializerService);

                switch (type) {
                    case 'json':
                        resolve(instance.children);
                        break;
                    case 'markdown':
                        resolve(mdSerializerService.serialize(instance.children, {standard: true, brCharacterInTable}));
                        break;
                    case 'text':
                        resolve(mdSerializerService.serialize(instance.children));
                        break;
                    case 'html':
                        resolve(htmlSerializerService.serialize(instance.children, {inDoc: true, inlineStyle}));
                        break;
                    default:
                        reject(new Error('不支持的文档类型'));
                }
            };

            getContent().then(resolve).catch(reject);
        });
    },
    // 获取文档的id
    getDocId: () => {
        const doc = store.getCurDocument();
        return doc?.docGuid;
    },
    // 获取文档的rte token
    getDocToken: () => {
        const {accessToken} = store.getData(store.KEYS.CUR_DOCUMENT_TOKEN);
        return accessToken;
    },
    // 获取文档基础信息
    getDocBaseInfo: () => {
        const curDocument = store.getData(store.KEYS.CUR_DOCUMENT) || {};
        const {version, publishTime, modified, docGuid, publisherNickname, name, created, nickname} = curDocument;

        return {
            version,
            updateTime: publishTime || modified,
            updater: publisherNickname,
            docUrl: window.location.href,
            docId: docGuid,
            title: name,
            createTime: created,
            creator: nickname,
        };
    },
    // 获取目录树数据
    getSideBarData: () => {
        const treeData = store.getSidebarTreeObj()?.value || [];

        if (!Array.isArray(treeData)) {
            return [];
        }

        return treeData.map(node => formatSideBarNode(node));
    },
    // 获取文档权限
    getDocPermission: () => {
        const {isAdmin, isMember, isReader} = KuAuthority.getAuthDocument();

        const curDocument = store.getData(store.KEYS.CUR_DOCUMENT) || {};
        const docScope = curDocument.scope;
        const inheritRepo = curDocument.inheritRepo;
        const curRepository = store.getData(store.KEYS.CUR_REPOSITORY) || {};
        const repoScope = curRepository.scope;

        // 知识库是否公开
        const isRepoPublic = (inheritRepo !== InheritRepo.Not_Inherit) && (repoScope === RepositoryScope.SpaceReader);

        // 文档是否公开
        const isPublic = docScope === PageScope.PagePublic || docScope === PageScope.PagePublicEdit || isRepoPublic;
        // 文档是否可编辑
        const isPublicEdit = docScope === PageScope.PagePublicEdit;

        return {
            isAdmin,
            isMember,
            isReader,
            isPublic,
            isPublicEdit,
        };
    },
    // 获取文档模式
    getDocMode: () => {
        const mode: DOC_MODE = store.getData(store.KEYS.DOCUMENT_MODE);
        return mode;
    },
    // 获取文档静态化 html
    getDocStaticHtml: (options = {}) => {
        const editorRef = store.getEditorRef();
        const serialize = getMorphoService(serviceNames.IHtmlSerializerService)?.serialize;

        if (!editorRef.current || typeof editorRef.current.getEditorInstance !== 'function' || !serialize) {
            // 待增加错误上报埋点逻辑
            return '';
        }

        try {
            const instance = editorRef.current.getEditorInstance();
            const jsonContent = instance.children;

            // 暂时仅支持文档模式，后续补充卡片模式支持
            const serializeOptions: any = {
                inlineStyle: true,
                inDoc: true,
                hideCard: true,
                ...options,
            };

            // const themeColor = getMorphoService(serviceNames.ICoverService)?.getThemeColor();
            // if (themeColor) {
            //     serializeOptions.theme = themeColor;
            // }

            const html = serialize(jsonContent, serializeOptions);

            return html;
        } catch (e) {
            // 待增加错误上报埋点逻辑
            return '';
        }
    },
    // 获取文档附件数据
    getDocAttachData: async () => {
        const editorRef = store.getEditorRef();

        if (!editorRef.current || typeof editorRef.current.getEditorInstance !== 'function') {
            // 待增加错误上报埋点逻辑
            return [];
        }

        try {
            const instance = editorRef.current.getEditorInstance();
            const jsonContent = instance.children;

            // 遍历 jsonContent，获取附件数据
            const attachData = getAttachDataByJSON(jsonContent);

            if (attachData.length > 0) {
                const urlList = await Promise.all(attachData.map(item => getAttachUrl(item.id)));

                urlList.forEach((url, index) => {
                    if (url) {
                        attachData[index].url = url;
                    }
                });
            }

            return attachData;
        } catch (e) {
            // 待增加错误上报埋点逻辑
            return [];
        }
    },
    // 获取文档是否处于演示模式
    getIsDemoMode() {
        const isDemoMode: boolean = store.getData(store.KEYS.DEMO_MODE);
        return isDemoMode;
    }
};
